// src/components/contact/EnquiryForm.jsx
import React, { useState, useEffect, Fragment } from "react";
import { getCountries, postOnlineEnquiry } from "../../api/apiService";
import { useLocale } from "../../contexts/LocaleContext";
import { InputField, SelectField } from "./EnquiryFields";
import FeedbackModal from "../common/FeedbackModal";

const EnquiryForm = ({ enquiryData }) => {
  const { locale } = useLocale();
  const {
    title,
    subtitle,
    name: nameLabel,
    company: companyLabel,
    email: emailLabel,
    enquiry: enquiryLabel,
    country: countryLabel,
    telephone: telephoneLabel,
    compulsory_fields: compulsoryText,
    submit: submitText,
    reset: resetText,
    success: successText,
    fail: failedText,
    please_select: pleaseSelectText,
  } = enquiryData || {};

  let initialFormData = {
    name: "",
    company: "",
    email: "",
    country: "",
    phone: "",
    enquiry: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const [countries, setCountries] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackModalProps, setFeedbackModalProps] = useState({
    title: "",
    type: "info", // success, error, info, warning
  });

  useEffect(() => {
    const fetchCountries = async () => {
      if (!locale) {
        setIsLoadingCountries(false);
        setCountries([]);
        return;
      }
      setIsLoadingCountries(true);
      try {
        const countriesData = await getCountries(locale);
        const formattedCountries = (countriesData || []).map((country) => {
          const countryName = country.label || "Unknown Country";
          return { label: countryName, value: countryName };
        });
        setCountries(formattedCountries || []);
      } catch (error) {
        console.error("Failed to fetch countries for enquiry form:", error);
        setCountries([]);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    if (locale) {
      fetchCountries();
    }
  }, [locale]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await postOnlineEnquiry({
        ...formData,
        domain: "Aqutek.Asia",
      });

      if (response && response.success) {
        setFeedbackModalProps({
          title: successText,
          type: "success",
        });
        setFormData(initialFormData);
      } else {
        setFeedbackModalProps({
          title: failedText,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setFeedbackModalProps({
        title: failedText,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
      setShowFeedbackModal(true);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      country: "",
      phone: "",
      enquiry: "",
    });
  };

  if (!enquiryData)
    return (
      <div className="p-6 text-center text-gray-500">
        Loading form configuration...
      </div>
    );
  return (
    <>
      <section className="py-10 md:py-16 bg-gray-100">
        {" "}
        <div className="container mx-auto px-4 max-w-3xl">
          {" "}
          <div className="bg-white p-6 md:p-8 shadow-xl rounded-lg border border-gray-200">
            <h2 className="text-2xl md:text-3xl font-bold text-[#A00000] mb-1 text-center md:text-left">
              {" "}
              {title || "Online Enquiry"}
            </h2>
            {subtitle && (
              <p className="text-gray-600 mb-6 text-sm text-center md:text-left">
                {subtitle}
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6">
                <InputField
                  label={nameLabel || "Name"}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <InputField
                  label={companyLabel || "Company"}
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6">
                <InputField
                  label={emailLabel || "Email"}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {pleaseSelectText ? (
                  <SelectField
                    label={countryLabel || "Country / Region"}
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    options={countries}
                    disabled={isLoadingCountries}
                    placeholder={
                      isLoadingCountries
                        ? "Loading countries..."
                        : pleaseSelectText || "-- Please select --"
                    }
                    required
                  />
                ) : (
                  <InputField
                    label={countryLabel || "Country / Region"}
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                )}
              </div>
              <InputField
                label={telephoneLabel || "Telephone"}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
              />
              <InputField
                label={enquiryLabel || "Enquiry"}
                name="enquiry"
                type="textarea"
                value={formData.enquiry}
                onChange={handleChange}
                required
              />

              {compulsoryText && (
                <p className="text-xs text-red-600 mb-4 text-right">
                  * {compulsoryText}
                </p>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full sm:w-auto px-6 py-2.5 border border-gray-400 text-gray-700 font-medium rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  {resetText || "Reset"}
                </button>
                <button
                  type="submit"
                  className={`w-full sm:w-auto px-8 py-2.5 font-semibold rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 bg-[#A00000]`}
                >
                  {submitText || "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title={feedbackModalProps.title}
        type={feedbackModalProps.type}
      />
    </>
  );
};

export default EnquiryForm;
